import React, { useState, useRef } from 'react';
import { JSX } from 'react/jsx-runtime';
import {
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Button,
  TableCellLayout,
  Spinner,
} from '@fluentui/react-components';
import {
  Rename20Filled,
  Delete20Filled,
  ArrowDownload20Filled,
  ShieldLock20Regular,
} from '@fluentui/react-icons';
import { IContainerItem } from '../../common/ContainerSchemas';
import { ManageAccessDialog } from '../../dialogs/manage-access-dialog';
import { RenameDialog } from '../../dialogs/RenameDialog';
import { DeleteDialog } from '../../dialogs/DeleteDialog';
import { ContainerItemPreview } from '../container-item-preview';
import { ItemsApiProvider } from '../../providers/ItemsApiProvider';
import { ItemPermissionsProvider } from '../../providers/ItemPermissionsProvider';
import './ContainerItemActions.css';
import { ApiError } from '../../types/ApiError';

const itemsApi = ItemsApiProvider.instance;

interface ContainerItemActionsProps {
  item: IContainerItem;
  stopPropagation: (e: React.MouseEvent) => void;
  containerId: string;
  onItemsUpdated?: () => void;
}

// IconWrapper component moved outside of MoreIcon
const IconWrapper = ({ children }: { children: React.ReactNode }) => (
  <span className="more-icon-wrapper">{children}</span>
);

// Custom MoreIcon isolated from styling issues
const MoreIcon = (): JSX.Element => {
  return (
    <IconWrapper>
      <svg
        width="20"
        height="22"
        viewBox="0 0 20 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="more-icon-svg"
      >
        <circle cx="10" cy="3" r="1.5" fill="currentColor" />
        <circle cx="10" cy="10" r="1.5" fill="currentColor" />
        <circle cx="10" cy="17" r="1.5" fill="currentColor" />
      </svg>
    </IconWrapper>
  );
};

export const ContainerItemActions: React.FC<ContainerItemActionsProps> = ({
  item,
  stopPropagation,
  containerId: containerId,
  onItemsUpdated,
}) => {
  const [showManageAccessDialog, setShowManageAccessDialog] =
    useState<boolean>(false);
  const [itemToManage, setItemToManage] = useState<IContainerItem | undefined>(
    undefined,
  );

  // Reference to ManageAccessDialog to call its focus method
  const manageAccessDialogRef = useRef<{ focus: () => void } | null>(null);

  // Rename state
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');
  const [showRenamingSpinner, setShowRenamingSpinner] =
    useState<boolean>(false);

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showDeletingSpinner, setShowDeletingSpinner] =
    useState<boolean>(false);
  const [permissionToDelete, setPermissionToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showPermissionDeleteDialog, setShowPermissionDeleteDialog] =
    useState(false);
  const [accessRefreshCounter, setAccessRefreshCounter] = useState(0);
  const [showPermissionDeletingSpinner, setShowPermissionDeletingSpinner] =
    useState(false);

  // Download state
  const [showDownloadingSpinner, setShowDownloadingSpinner] =
    useState<boolean>(false);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  // Error state
  const [error, setError] = useState<Error | null>(null);

  const handleManageAccess = (item: IContainerItem, e: React.MouseEvent) => {
    // Prevent the row selection from changing
    stopPropagation(e);

    // Set the item to manage and show the dialog
    setItemToManage(item);
    setShowManageAccessDialog(true);
  };

  const handleManageAccessDismiss = () => {
    setShowManageAccessDialog(false);
    setItemToManage(undefined);
  };

  // Rename functions
  const onRenameClick = (e: React.MouseEvent) => {
    stopPropagation(e);
    setNewName(item.name);
    setShowRenameDialog(true);
  };

  const renameItem = async (updatedName: string) => {
    setShowRenamingSpinner(true);

    try {
      await itemsApi.rename(containerId, item.id, updatedName);
      item.name = updatedName;
      onItemsUpdated?.();
    } catch (err) {
      setError(
        new Error(
          `Failed to rename item. ${err instanceof ApiError ? err.detail : 'Unknown error.'}`,
        ),
      );
    } finally {
      setShowRenamingSpinner(false);
      setNewName('');
      setShowRenameDialog(false);
    }
  };

  const closeRenameModal = () => {
    setShowRenameDialog(false);
  };

  // Delete functions
  const onDeleteClick = (e: React.MouseEvent) => {
    stopPropagation(e);
    setShowDeleteDialog(true);
  };

  const deleteItem = async () => {
    setShowDeletingSpinner(true);
    // Store the current item name for error handling to avoid mutation effects
    const itemName = item.name;
    try {
      await itemsApi.delete(containerId, item.id);
      onItemsUpdated?.();
    } catch (err) {
      setError(
        new Error(
          `Failed to delete item "${itemName}". Please check your permissions or try again later.`,
        ),
      );
    } finally {
      setShowDeletingSpinner(false);
      setShowDeleteDialog(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteDialog(false);
  };

  // File operations

  const onDownloadClick = async (e: React.MouseEvent) => {
    stopPropagation(e);
    if (item.type === 'folder') {
      return;
    }

    setShowDownloadingSpinner(true);
    try {
      const blob = await itemsApi.download(containerId, item.id);

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        new Error(`Failed to download file "${item.name}". Please try again.`),
      );
    } finally {
      setShowDownloadingSpinner(false);
      // Close the menu after download completes
      setIsMenuOpen(false);
    }
  };

  const handleDeletePermission = async () => {
    if (!itemToManage || !permissionToDelete) return;
    setShowPermissionDeletingSpinner(true);
    try {
      await ItemPermissionsProvider.instance.deletePermission(
        containerId,
        itemToManage.id,
        permissionToDelete.id,
      );
      setAccessRefreshCounter((prev) => prev + 1);
    } catch (err) {
      console.error('Error deleting permission:', err);
    } finally {
      setShowPermissionDeleteDialog(false);
      setPermissionToDelete(null);
      setShowPermissionDeletingSpinner(false);
    }
  };

  const handleDialogClosed = () => {
    // Focus the ManageAccessDialog using the ref
    setTimeout(() => {
      if (showManageAccessDialog && manageAccessDialogRef.current) {
        manageAccessDialogRef.current.focus();
      }
    }, 50);
  };

  const handleManageAccessDialogMounted = (ref: { focus: () => void }) => {
    manageAccessDialogRef.current = ref;
  };

  if (error) {
    throw error;
  }

  return (
    <>
      <TableCellLayout>
        <Menu
          open={isMenuOpen}
          onOpenChange={(e, data) => {
            setIsMenuOpen(data.open);
          }}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              icon={<MoreIcon />}
              appearance="transparent"
              aria-label="More options"
              onClick={(e) => stopPropagation(e)}
              className={`container-item-action-button ${isMenuOpen ? 'menu-open' : ''}`}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <ContainerItemPreview
                item={item}
                stopPropagation={stopPropagation}
                containerId={containerId}
              />
              {item.type !== 'folder' && (
                <>
                  <MenuItem
                    icon={
                      showDownloadingSpinner ? (
                        <Spinner size="extra-tiny" />
                      ) : (
                        <ArrowDownload20Filled />
                      )
                    }
                    onClick={(e) => onDownloadClick(e)}
                    data-testid="menu-item-download"
                    disabled={showDownloadingSpinner}
                    persistOnClick
                  >
                    {showDownloadingSpinner ? 'Downloading...' : 'Download'}
                  </MenuItem>
                </>
              )}
              <MenuItem
                icon={<Rename20Filled />}
                onClick={(e) => onRenameClick(e)}
                data-testid="menu-item-rename"
              >
                Rename
              </MenuItem>
              <MenuItem
                icon={<Delete20Filled />}
                onClick={(e) => onDeleteClick(e)}
                data-testid="menu-item-delete"
              >
                Delete
              </MenuItem>
              {
                <MenuItem
                  icon={<ShieldLock20Regular />}
                  onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                    handleManageAccess(item, e)
                  }
                >
                  Manage Access
                </MenuItem>
              }
            </MenuList>
          </MenuPopover>
        </Menu>
      </TableCellLayout>

      {
        <ManageAccessDialog
          selectedItem={itemToManage}
          showManageAccessDialog={showManageAccessDialog}
          onDismiss={handleManageAccessDismiss}
          containerId={containerId}
          refreshCounter={accessRefreshCounter}
          onPermissionAdded={() => setAccessRefreshCounter((prev) => prev + 1)}
          onManageAccessDialogMounted={handleManageAccessDialogMounted}
          onRequestDeletePermission={(permission) => {
            setPermissionToDelete({
              id: permission.id,
              name:
                permission.user_principal_name ||
                permission.group_name ||
                'Unknown user',
            });
            setShowPermissionDeleteDialog(true);
          }}
        />
      }
      <RenameDialog
        newName={newName}
        showRenameDialog={showRenameDialog}
        showRenamingSpinner={showRenamingSpinner}
        selectedItem={item}
        onItemsUpdated={onItemsUpdated || (() => {})}
        renameItem={renameItem}
        closeRenameModal={closeRenameModal}
      />

      <DeleteDialog
        showDeleteDialog={showDeleteDialog}
        showDeletingSpinner={showDeletingSpinner}
        selectedItem={item.name}
        onItemsUpdated={onItemsUpdated || (() => {})}
        deleteItem={deleteItem}
        closeDeleteModal={closeDeleteModal}
      />

      <DeleteDialog
        showDeleteDialog={showPermissionDeleteDialog}
        showDeletingSpinner={showPermissionDeletingSpinner}
        selectedItem={permissionToDelete?.name || ''}
        onItemsUpdated={() => {}}
        deleteItem={handleDeletePermission}
        closeDeleteModal={() => {
          setShowPermissionDeleteDialog(false);
          setPermissionToDelete(null);
        }}
        onDialogClosed={handleDialogClosed}
      />
    </>
  );
};

export default ContainerItemActions;
